import { faCheck } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { Node } from '@react-types/shared';
import { FC, ReactNode, useEffect, useRef } from 'react';
import { AriaListBoxOptions, useListBox, useOption } from 'react-aria';
import { ListState } from 'react-stately';
import { EmptyValue } from 'src/components/data-display/empty-value';
import { SelectOption } from 'src/components/form/select';
import { Stack } from 'src/components/layout/stack';
import { Colors, flyoutStyles } from 'src/styles';
import styled, { css } from 'styled-components';

export const ListBoxContainer = styled.div`
  ${flyoutStyles}
  left: 0;
  right: 0;
  min-width: 17.9rem;
  max-height: 23.5rem;
  overflow: auto;
`;

// apply our global default styles for lists by generating a `class`
const List = styled.ul``;

type ListBoxProps = {
  state: ListState<SelectOption<string | null>>;
  name: string;
  ariaListBoxProps: AriaListBoxOptions<SelectOption<string | null>>;
  prepend?: ReactNode;
};

export const ListBox: FC<ListBoxProps> = (props) => {
  const { state, name, ariaListBoxProps, prepend } = props;

  const ref = useRef<HTMLDivElement>(null);
  const { listBoxProps } = useListBox(ariaListBoxProps, state, ref);

  useEffect(() => {
    const firstKey = state.selectionManager.selectedKeys.keys().next().value;
    state.selectionManager.setFocusedKey(firstKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const options = [...state.collection];

  return (
    <ListBoxContainer
      data-testid={`${name}-options`}
      {...listBoxProps}
      ref={ref}
    >
      {prepend}

      <List>
        {options.map((item) => (
          <Option
            disabled={item.value.disabled}
            name={name}
            key={item.key}
            item={item}
            state={state}
          />
        ))}
      </List>
    </ListBoxContainer>
  );
};

export const ListItem = styled.li<{
  disabled?: boolean;
  isFocused?: boolean;
  isSelected?: boolean;
}>`
  list-style: none;
  color: ${Colors.brand};
  cursor: default;
  word-break: break-all;
  margin: 0.1rem;
  border-radius: 0.4rem;

  ${({ isSelected }) =>
    isSelected &&
    css`
      background-color: ${Colors.background};
    `}

  ${({ isFocused, isSelected }) =>
    isFocused &&
    !isSelected &&
    css`
      background-color: ${Colors.brandLight3};
    `}

  padding: 1rem;
  padding-left: 2rem;

  ${({ disabled }) =>
    disabled &&
    css`
      border-color: ${Colors.inactive};
      background: ${Colors.inactiveLight};
      color: ${Colors.inactive};
      cursor: not-allowed;

      ::placeholder {
        color: ${Colors.inactive};
      }
    `}
`;

type OptionProps = {
  item: Node<SelectOption<string | null>>;
  state: ListState<SelectOption<string | null>>;
  name: string;
  disabled?: boolean;
};

const Option: FC<OptionProps> = ({ item, state, name, disabled }) => {
  const ref = useRef<HTMLLIElement>(null);

  const { optionProps, isSelected, isFocused } = useOption(
    { key: item.key },
    state,
    ref
  );

  return (
    <ListItem
      {...optionProps}
      onPointerLeave={(event) => {
        state.selectionManager.setFocusedKey(null);
        optionProps.onPointerLeave?.(event);
      }}
      isSelected={isSelected}
      ref={ref}
      disabled={disabled}
      isFocused={isFocused}
      data-testid={`${name}-option-${item.value.value}`}
    >
      <Stack
        flow="column"
        justifyContent="space-between"
        alignItems="baseline"
        gap={2}
      >
        {item.value.value === '' ? (
          <EmptyValue label={item.textValue} />
        ) : (
          item.rendered
        )}

        {isSelected && item.value.value !== '' && (
          <FontAwesomeIcon
            data-testid={`${name}-option-${item.value.value}-isSelected`}
            icon={faCheck}
          />
        )}
      </Stack>
    </ListItem>
  );
};
